import { useState } from "react";
import { toast } from "sonner";
import { Plus, Library, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BrandButton } from "@/components/PageHeader";
import { SponsorDialog } from "@/components/sponsors/SponsorDialog";
import { SponsorRow, swap } from "@/components/sponsors/SponsorRow";
import { SponsorLogo } from "@/components/SponsorsCarousel";
import { SetupSection } from "./BasicsSection";
import { api, shortId } from "@/lib/api";

const LibraryPicker = ({ sponsors, onPick }) => {
  const [list, setList] = useState(null);
  const load = (open) => open && api.get("/sponsors?all=true").then((r) => setList(r.data));
  const used = new Set(sponsors.map((s) => s.source_id).filter(Boolean));
  return (
    <Popover onOpenChange={load}>
      <PopoverTrigger asChild>
        <BrandButton type="button" variant="outline" data-testid="setup-sponsor-library-button"><Library className="w-4 h-4" />Pick from general sponsors</BrandButton>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start" data-testid="setup-sponsor-library">
        {!list ? <div className="text-sm text-gray-500 p-2">Loading…</div> : list.length === 0 ? <div className="text-sm text-gray-500 p-2">No general sponsors yet.</div> : (
          <ul className="max-h-72 overflow-y-auto">
            {list.map((s) => (
              <li key={s.id}>
                <button type="button" disabled={used.has(s.id)} onClick={() => onPick(s)} className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-[#FFF0F0] disabled:opacity-50 disabled:hover:bg-transparent text-left" data-testid={`setup-sponsor-library-item-${s.id}`}>
                  <span className="h-10 w-20 grid place-items-center bg-[#F8F9FA] rounded border border-[#E5E7EB] overflow-hidden"><SponsorLogo s={s} className="h-8 text-[10px]" /></span>
                  <span className="flex-1 text-sm font-medium">{s.name}</span>
                  {used.has(s.id) && <Check className="w-4 h-4 text-emerald-600" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const SponsorsSection = ({ sponsors, onChange }) => {
  const [dialog, setDialog] = useState({ open: false, sponsor: null });
  const pick = ({ id, name, logo, phone, email, website }) => {
    onChange([...sponsors, { id: shortId(), source_id: id, name, logo, phone, email, website, active: true }]);
    toast.success(`${name} added to this event`);
  };
  const submit = (body) => {
    const s = dialog.sponsor;
    onChange(s ? sponsors.map((x) => (x.id === s.id ? { ...x, ...body } : x)) : [...sponsors, { id: shortId(), source_id: null, ...body }]);
  };
  return (
    <SetupSection id="sponsors" step="06" title="Event sponsors" description="Sponsors for this event only. They appear on this event's homepage slide, the registration page and payment receipts. The homepage sponsor strip keeps showing the general sponsors.">
      <div className="flex flex-wrap gap-2">
        <LibraryPicker sponsors={sponsors} onPick={pick} />
        <BrandButton type="button" variant="outline" onClick={() => setDialog({ open: true, sponsor: null })} data-testid="setup-sponsor-add-button"><Plus className="w-4 h-4" />Add event-only sponsor</BrandButton>
      </div>
      {sponsors.length === 0 ? <p className="text-sm text-gray-500 mt-4" data-testid="setup-sponsors-empty">No sponsors for this event yet.</p> : (
        <ul className="mt-4" data-testid="setup-sponsors-list">
          {sponsors.map((s, i) => (
            <SponsorRow key={s.id} s={s} i={i} total={sponsors.length} prefix="setup-sponsor" tag={s.source_id ? "General list" : "Event only"}
              move={(idx, d) => onChange(swap(sponsors, idx, d))} edit={(sp) => setDialog({ open: true, sponsor: sp })}
              toggle={(sp) => onChange(sponsors.map((x) => (x.id === sp.id ? { ...x, active: !x.active } : x)))}
              remove={(sp) => onChange(sponsors.filter((x) => x.id !== sp.id))} />
          ))}
        </ul>
      )}
      <SponsorDialog open={dialog.open} sponsor={dialog.sponsor} onClose={() => setDialog({ open: false, sponsor: null })} onSubmit={submit} showLabel="Show for this event" />
    </SetupSection>
  );
};
